import { createSubjectAttributes, ExperimentationProvider, Platform, ProjectName } from '@user-portal/experimentation';
import { useBootstrapNavigation } from 'navigation/useBootstrapNavigation';
import { FC, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectCurrentUser } from 'store/user/selectors';

import { experimentationConfig } from './experimentationConfig';

interface ExperimentationWrapperProps {
    children: ReactNode;
}

/**
 * Lives inside the ExperimentationProvider so it can read the Eppo context.
 * Resolves nav flags + `/features` once on app open / page refresh (after auth).
 */
const NavigationBootstrap: FC<{ children: ReactNode }> = ({ children }) => {
    useBootstrapNavigation();

    return <>{children}</>;
};

export const ExperimentationWrapper: FC<ExperimentationWrapperProps> = ({ children }) => {
    const user = useSelector(selectCurrentUser);

    const subjectAttributes = useMemo(() => {
        const userId = user.core_id?.toString() || 'anonymous';
        const userEmail = user.email || '';

        return createSubjectAttributes(userId, userId, {
            // Application information
            platform: Platform.WEB,
            source_project: ProjectName.MENSCOACH,
            source_platform: Platform.WEB,

            // User properties
            country: user.country || undefined,
            gender: user.gender || undefined,
            height: user.height || undefined,
            weight: user.weight || undefined,
            scale: user.units || undefined,

            // Fitness specific
            fitness_level: user.fitness_level || undefined,
            goal: user.goal || undefined,
            target_weight: user.target_weight || undefined,
            is_active_plan: user.is_paid || false,
            problem_zones: user.target_zone || undefined,

            extras: {
                email: userEmail,
                source: 'muscle-booster-portal',
            },
        });
    }, [user]);

    return (
        <ExperimentationProvider config={experimentationConfig} subjectAttributes={subjectAttributes}>
            <NavigationBootstrap>{children}</NavigationBootstrap>
        </ExperimentationProvider>
    );
};
